import React, { useCallback, useEffect, useState } from 'react';
import {
  Badge, Card, Checkbox, Dropdown, Empty, Input, Modal, Popconfirm, Select, Space, Tabs, Tag,
  Typography, message,
} from 'antd';
import {
  CheckOutlined, DeleteOutlined, FilterOutlined, PlusOutlined, ReloadOutlined, UndoOutlined,
  WarningFilled,
} from '@ant-design/icons';
import { ColumnsType } from 'antd/lib/table';
import dayjs from 'dayjs';
import { Button } from '@app/components/common/buttons/Button/Button';
import { Card as CommonCard } from '@app/components/common/Card/Card';
import { Table, DEFAULT_PAGE_SIZE, PAGE_SIZE_OPTIONS } from '@app/components/common/Table/Table';
import { PageTitle, Toolbar, ToolbarActions } from '@app/components/common/PageToolbar/PageToolbar';
import { notificationController } from '@app/controllers/notificationController';
import {
  bulkFlagSerials, createFlaggedSerial, deleteFlaggedSerial, getFlagStats, getFlaggedHits,
  getFlaggedSerials, reopenFlaggedHit, resolveFlaggedHit,
} from '@app/api/admin.api';
import { FlagReason, FlagStats, FlaggedSerial, FlaggedSerialHit, REASON_COLORS } from './types';

const { Text } = Typography;

const REASONS = [
  { label: 'Lost in transit', value: 'lost' },
  { label: 'Stolen', value: 'stolen' },
  { label: 'Other', value: 'other' },
];

const fmt = (iso: string | null) =>
  iso ? dayjs(iso).format('MMM D, YYYY') : '—';

const Serial: React.FC<{ value?: string | null }> = ({ value }) =>
  value ? (
    <Text code style={{ whiteSpace: 'nowrap' }}>{value}</Text>
  ) : (
    <Text type="secondary">—</Text>
  );

const StatTile: React.FC<{ label: string; value: number; tone: string }> = ({ label, value, tone }) => (
  <div
    style={{
      flex: 1, minWidth: 140, padding: '12px 14px', borderRadius: 10,
      background: 'var(--soft-panel-bg)', border: 'var(--soft-panel-border)',
    }}
  >
    <div style={{ fontSize: 21, fontWeight: 600, color: tone }}>{value.toLocaleString()}</div>
    <div style={{ fontSize: 12, opacity: 0.65 }}>{label}</div>
  </div>
);

const FlaggedSerialsPage: React.FC = () => {
  const [stats, setStats] = useState<FlagStats | null>(null);

  // --- serial list ---------------------------------------------------------
  const [serials, setSerials] = useState<FlaggedSerial[]>([]);
  const [serialCount, setSerialCount] = useState(0);
  const [serialPage, setSerialPage] = useState(1);
  const [serialSize, setSerialSize] = useState(DEFAULT_PAGE_SIZE);
  const [serialSearch, setSerialSearch] = useState('');
  const [reasons, setReasons] = useState<string[]>([]);
  const [filterOpen, setFilterOpen] = useState(false);
  const [loadingSerials, setLoadingSerials] = useState(false);

  // --- detections ----------------------------------------------------------
  const [hits, setHits] = useState<FlaggedSerialHit[]>([]);
  const [hitCount, setHitCount] = useState(0);
  const [hitPage, setHitPage] = useState(1);
  const [hitSize, setHitSize] = useState(DEFAULT_PAGE_SIZE);
  const [unresolvedOnly, setUnresolvedOnly] = useState(true);
  const [loadingHits, setLoadingHits] = useState(false);

  // --- add dialog ----------------------------------------------------------
  const [addOpen, setAddOpen] = useState(false);
  const [bulkMode, setBulkMode] = useState(false);
  const [form, setForm] = useState<{
    serial: string;
    reason: FlagReason;
    replacement_serial: string;
    original_order_number: string;
    notes: string;
  }>({
    serial: '', reason: 'lost', replacement_serial: '', original_order_number: '', notes: '',
  });
  const [bulkText, setBulkText] = useState('');
  const [saving, setSaving] = useState(false);

  const loadStats = useCallback(() => {
    getFlagStats().then(setStats).catch(() => undefined);
  }, []);

  const loadSerials = useCallback((page = 1, size = DEFAULT_PAGE_SIZE) => {
    setLoadingSerials(true);
    getFlaggedSerials({
      page,
      page_size: size,
      search: serialSearch.trim() || undefined,
      reason: reasons.length ? reasons.join(',') : undefined,
    })
      .then((data) => {
        setSerials(data.results || []);
        setSerialCount(data.count || 0);
      })
      .catch((e) => notificationController.error({ message: e.message }))
      .finally(() => setLoadingSerials(false));
  }, [serialSearch, reasons]);

  const loadHits = useCallback((page = 1, size = DEFAULT_PAGE_SIZE) => {
    setLoadingHits(true);
    getFlaggedHits({
      page,
      page_size: size,
      is_resolved: unresolvedOnly ? 'false' : undefined,
    })
      .then((data) => {
        setHits(data.results || []);
        setHitCount(data.count || 0);
      })
      .catch((e) => notificationController.error({ message: e.message }))
      .finally(() => setLoadingHits(false));
  }, [unresolvedOnly]);

  const refresh = useCallback(() => {
    loadStats();
    loadSerials(serialPage, serialSize);
    loadHits(hitPage, hitSize);
  }, [loadStats, loadSerials, loadHits, serialPage, serialSize, hitPage, hitSize]);

  useEffect(() => { loadStats(); }, [loadStats]);
  useEffect(() => { setSerialPage(1); loadSerials(1, serialSize); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [serialSearch, reasons]);
  useEffect(() => { setHitPage(1); loadHits(1, hitSize); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [unresolvedOnly]);

  const onAdd = () => {
    setSaving(true);
    const done = (added: number) => {
      setAddOpen(false);
      setForm({ serial: '', reason: 'lost', replacement_serial: '', original_order_number: '', notes: '' });
      setBulkText('');
      refresh();
      if (added) message.success(`${added} serial${added === 1 ? '' : 's'} flagged`);
    };

    if (bulkMode) {
      bulkFlagSerials(bulkText, form.reason, form.notes)
        .then((res) => {
          done(res.added.length);
          // Surfaced rather than swallowed: a typo here means a serial nobody
          // will ever be alerted about.
          if (res.invalid.length) {
            notificationController.warning({
              message: `Not recognised as serials: ${res.invalid.join(', ')}`,
            });
          }
          if (res.already_flagged.length) {
            message.info(`Already flagged: ${res.already_flagged.join(', ')}`);
          }
        })
        .catch((e) => notificationController.error({ message: e.message }))
        .finally(() => setSaving(false));
      return;
    }

    createFlaggedSerial(form)
      .then(() => done(1))
      .catch((e) =>
        notificationController.error({
          message: e?.response?.data?.serial?.[0] || e.message,
        }))
      .finally(() => setSaving(false));
  };

  const serialColumns: ColumnsType<FlaggedSerial> = [
    {
      title: 'Serial',
      dataIndex: 'serial',
      width: 150,
      render: (v: string) => <Serial value={v} />,
    },
    {
      title: 'Reason',
      dataIndex: 'reason',
      width: 120,
      render: (_: string, r) => <Tag color={REASON_COLORS[r.reason]}>{r.reasonLabel}</Tag>,
    },
    {
      title: 'Replacement',
      dataIndex: 'replacement_serial',
      width: 150,
      render: (v: string) => <Serial value={v} />,
    },
    { title: 'Order', dataIndex: 'original_order_number', width: 100, render: (v: string) => v || '—' },
    {
      title: 'Detections',
      dataIndex: 'hitCount',
      width: 110,
      align: 'right',
      render: (v: number) =>
        v ? <Tag color="red">{v}</Tag> : <Text type="secondary">0</Text>,
    },
    { title: 'Reported', dataIndex: 'reported_at', width: 120, render: fmt },
    { title: 'Notes', dataIndex: 'notes', ellipsis: true, render: (v: string) => v || '—' },
    {
      title: '',
      width: 110,
      align: 'right',
      render: (_: unknown, r) => (
        <Popconfirm
          title={
            r.hitCount
              ? `Remove this flag and its ${r.hitCount} detection${r.hitCount === 1 ? '' : 's'}?`
              : 'Remove this serial from the lost/stolen list?'
          }
          // Spelled out because detections cascade: deleting a flag that has
          // already caught someone throws that evidence away.
          okText="Delete"
          okButtonProps={{ danger: true }}
          onConfirm={() =>
            deleteFlaggedSerial(r.id).then(refresh).catch((e) =>
              notificationController.error({ message: e.message }))}
        >
          <Button size="small" danger type="text" icon={<DeleteOutlined />} className="row-action">
            Delete
          </Button>
        </Popconfirm>
      ),
    },
  ];

  const hitColumns: ColumnsType<FlaggedSerialHit> = [
    {
      title: 'Serial',
      dataIndex: 'serial',
      width: 150,
      render: (v: string, r) => (
        <Space size={4}>
          <WarningFilled style={{ color: r.reason === 'stolen' ? '#ff3d71' : '#ffaa00' }} />
          <Serial value={v} />
        </Space>
      ),
    },
    {
      title: 'Reason',
      dataIndex: 'reason',
      width: 110,
      render: (_: string, r) => <Tag color={REASON_COLORS[r.reason]}>{r.reasonLabel}</Tag>,
    },
    {
      title: 'Connected by',
      width: 220,
      ellipsis: true,
      render: (_: unknown, r) =>
        r.user ? (
          <div style={{ minWidth: 0 }}>
            <div>{r.user.name || 'No name'}</div>
            <Text type="secondary" style={{ fontSize: 12 }}>{r.user.email}</Text>
          </div>
        ) : (
          <Text type="secondary">—</Text>
        ),
    },
    {
      title: 'Also has replacement',
      dataIndex: 'alsoHasReplacement',
      width: 170,
      render: (v: boolean) =>
        v ? <Tag color="red">Yes — has both</Tag> : <Text type="secondary">No</Text>,
    },
    { title: 'Detected', dataIndex: 'detectedAt', width: 130, render: fmt },
    {
      title: 'Action',
      width: 120,
      align: 'right',
      render: (_: unknown, r) =>
        r.isResolved ? (
          <Button
            size="small"
            type="text"
            className="row-action"
            icon={<UndoOutlined />}
            onClick={() => reopenFlaggedHit(r.id).then(refresh)}
          >
            Reopen
          </Button>
        ) : (
          <Button
            size="small"
            type="primary"
            ghost
            className="row-action"
            icon={<CheckOutlined />}
            onClick={() => resolveFlaggedHit(r.id).then(refresh)}
          >
            Resolve
          </Button>
        ),
    },
  ];

  const filterPanel = (
    <div onClick={(e) => e.stopPropagation()}>
      <Card
        size="small"
        style={{ width: 360, maxWidth: '92vw', boxShadow: '0 6px 24px rgba(0,0,0,0.18)' }}
      >
        <div style={{ fontWeight: 600, marginBottom: 6 }}>Reason</div>
        <Checkbox.Group
          value={reasons}
          options={REASONS}
          onChange={(v) => setReasons(v as string[])}
          style={{ display: 'flex', flexDirection: 'column', gap: 4, marginBottom: 14 }}
        />
      </Card>
    </div>
  );

  const activeFilterCount = reasons.length ? 1 : 0;

  return (
    <>
      <Toolbar>
        <PageTitle>Lost &amp; Stolen Serials</PageTitle>
        <ToolbarActions>
          <Input
            style={{ width: 300 }}
            placeholder="Search serial, order, notes…"
            value={serialSearch}
            onChange={(e) => setSerialSearch(e.target.value)}
            allowClear
          />
          <Dropdown
            visible={filterOpen}
            onVisibleChange={setFilterOpen}
            trigger={['click']}
            placement="bottomRight"
            overlay={filterPanel}
          >
            <Badge count={activeFilterCount} size="small">
              <Button icon={<FilterOutlined />}>Filters</Button>
            </Badge>
          </Dropdown>
          <Button icon={<ReloadOutlined />} onClick={refresh}>Refresh</Button>
          <Button type="primary" icon={<PlusOutlined />} onClick={() => setAddOpen(true)}>
            Flag a serial
          </Button>
        </ToolbarActions>
      </Toolbar>

      {stats && (
        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: 14 }}>
          <StatTile label="Flagged serials" value={stats.flagged_serials} tone="#34AAD0" />
          <StatTile label="Unresolved detections" value={stats.unresolved} tone="#ff3d71" />
          <StatTile label="Has both units" value={stats.also_has_replacement} tone="#ffaa00" />
          <StatTile label="Detections all time" value={stats.total} tone="#9A9B9F" />
        </div>
      )}

      <CommonCard id="flagged-serials" padding="0.5rem 0.75rem 0">
        <Tabs
          defaultActiveKey="detections"
          tabBarExtraContent={
            <Checkbox
              checked={unresolvedOnly}
              onChange={(e) => setUnresolvedOnly(e.target.checked)}
            >
              Unresolved only
            </Checkbox>
          }
        >
          {/* Detections first: this is the queue that needs acting on. */}
          <Tabs.TabPane tab={`Detections${hitCount ? ` (${hitCount})` : ''}`} key="detections">
            <Table
              rowKey="id"
              columns={hitColumns}
              dataSource={hits}
              loading={loadingHits}
              locale={{
                emptyText: <Empty description="No flagged cushions have connected" />,
              }}
              pagination={{
                current: hitPage,
                pageSize: hitSize,
                total: hitCount,
                pageSizeOptions: PAGE_SIZE_OPTIONS,
                onChange: (p, size) => {
                  const next = size || hitSize;
                  setHitPage(p);
                  setHitSize(next);
                  loadHits(p, next);
                },
              }}
            />
          </Tabs.TabPane>

          <Tabs.TabPane tab="Flagged serials" key="serials">
            <Table
              rowKey="id"
              columns={serialColumns}
              dataSource={serials}
              loading={loadingSerials}
              locale={{ emptyText: <Empty description="No serials flagged yet" /> }}
              pagination={{
                current: serialPage,
                pageSize: serialSize,
                total: serialCount,
                pageSizeOptions: PAGE_SIZE_OPTIONS,
                onChange: (p, size) => {
                  const next = size || serialSize;
                  setSerialPage(p);
                  setSerialSize(next);
                  loadSerials(p, next);
                },
              }}
            />
          </Tabs.TabPane>
        </Tabs>
      </CommonCard>

      <Modal
        title="Flag a lost or stolen serial"
        visible={addOpen}
        onCancel={() => setAddOpen(false)}
        onOk={onAdd}
        okText="Flag"
        confirmLoading={saving}
      >
        <Space direction="vertical" size="middle" style={{ width: '100%' }}>
          <Checkbox checked={bulkMode} onChange={(e) => setBulkMode(e.target.checked)}>
            Add several at once
          </Checkbox>

          {bulkMode ? (
            <div>
              <div style={{ fontWeight: 600, marginBottom: 6 }}>Serials</div>
              <Input.TextArea
                rows={5}
                value={bulkText}
                onChange={(e) => setBulkText(e.target.value)}
                placeholder={'One per line or comma separated\nE202001155\nE161600491'}
              />
            </div>
          ) : (
            <>
              <div>
                <div style={{ fontWeight: 600, marginBottom: 6 }}>Serial</div>
                <Input
                  value={form.serial}
                  onChange={(e) => setForm({ ...form, serial: e.target.value })}
                  placeholder="E202001155"
                />
              </div>
              <div>
                <div style={{ fontWeight: 600, marginBottom: 6 }}>
                  Replacement serial <Text type="secondary">(optional)</Text>
                </div>
                <Input
                  value={form.replacement_serial}
                  onChange={(e) => setForm({ ...form, replacement_serial: e.target.value })}
                  placeholder="Serial of the unit sent instead"
                />
              </div>
              <div>
                <div style={{ fontWeight: 600, marginBottom: 6 }}>
                  Original order <Text type="secondary">(optional)</Text>
                </div>
                <Input
                  value={form.original_order_number}
                  onChange={(e) => setForm({ ...form, original_order_number: e.target.value })}
                  placeholder="#10449"
                />
              </div>
            </>
          )}

          <div>
            <div style={{ fontWeight: 600, marginBottom: 6 }}>Reason</div>
            <Select
              value={form.reason}
              onChange={(v) => setForm({ ...form, reason: v as FlagReason })}
              options={REASONS}
              style={{ width: '100%' }}
            />
          </div>
          <div>
            <div style={{ fontWeight: 600, marginBottom: 6 }}>Notes</div>
            <Input.TextArea
              rows={2}
              value={form.notes}
              onChange={(e) => setForm({ ...form, notes: e.target.value })}
            />
          </div>

          <Text type="secondary" style={{ fontSize: 12 }}>
            Already-paired cushions are checked immediately. The app itself is unaffected —
            nothing is shown to the customer.
          </Text>
        </Space>
      </Modal>
    </>
  );
};

export default FlaggedSerialsPage;
